import { Create, SaveButtonProps } from "@refinedev/mui";
import { Box, MenuItem, TextField } from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useCreate,
  useNavigation,
  useNotification,
} from "@refinedev/core";
import { FieldTypeOptionsType } from "pages";
import { MuiFileInput } from "mui-file-input";
import { FileEntity, UserEntityRoleEnum } from "generated/api";

import { commonValues } from "constants/common.values";

export const userTypeOptions: FieldTypeOptionsType[] = [
  { label: "Collector", value: UserEntityRoleEnum.Collector },
  { label: "Admin", value: UserEntityRoleEnum.Admin },
];

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
  const {
    refineCore: { formLoading },
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm();

  const { mutate, isLoading, mutateAsync } = useCreate();

  const { open } = useNotification();

  const { list } = useNavigation();

  const uploadImage = async (
    image: File
  ): Promise<{
    image?: string;
  }> => {
    let error = "";

    try {
      const formData = new FormData();
      formData.append("files", image);
      const responseData = (
        await mutateAsync({
          resource: "files",
          values: formData,
        })
      ).data as FileEntity[] | undefined;

      if (responseData?.[0]?.url) {
        return { image: responseData[0].url };
      } else {
        error = responseData?.[0].error || "";
      }
    } catch (err) {
      error = (err as Error).message;
    }

    if (error) {
      open?.({
        type: "error",
        message: "Error uploading file: " + error,
      });
    }
    return {};
  };

  const saveButtonProps: SaveButtonProps = {
    onClick: handleSubmit(async (d) => {
      let imageUrl: string | undefined;
      if (d.image) {
        const { image } = await uploadImage(d.image);
        imageUrl = image;
      }

      mutate(
        {
          resource: `auth/register/${watch("type").toLowerCase()}`,
          values: { ...d, image: imageUrl || undefined },
          successNotification: (_, values) => {
            return {
              description: "Account created",
              //@ts-ignore
              message: `${values?.type?.toUpperCase?.()} successfully created.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message,
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => list("users"),
        }
      );
    }),
    disabled: isLoading,
  };

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={saveButtonProps}
      title={"Create User"}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />

        <TextField
          {...register("email", {
            required: "This field is required",
            // eslint-disable-next-line no-useless-escape
            pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
          })}
          error={!!(errors as any)?.email}
          helperText={(errors as any)?.email?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email"
          name="email"
        />

        <TextField
          {...register("phoneNumber", {
            required: "This field is required",
            pattern: /^[0-9]{10}$/,
          })}
          error={!!(errors as any)?.phoneNumber}
          helperText={(errors as any)?.phoneNumber?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Phone Number"
          name="phoneNumber"
        />

        <TextField
          {...register("password", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.password}
          helperText={(errors as any)?.password?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Password"
          name="password"
          type="password"
        />

        <MuiFileInput
          label="Image"
          name="image"
          multiple={false}
          placeholder={"Select an image"}
          inputProps={{
            accept: "image/*",
          }}
          value={watch("image")}
          onChange={(file) => {
            if (!file) {
              setValue("image", undefined);
              return;
            }

            const hasExceededSize =
              file.size / (1024 * 1024) > commonValues.maxFileSize;

            if (hasExceededSize) {
              open?.({
                type: "error",
                message: `Max file size is ${commonValues.maxFileSize} MB`,
              });
              return;
            }

            setValue("image", file);
          }}
        />

        <TextField
          {...register("type", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.type}
          helperText={(errors as any)?.type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Account Type"
          name="type"
          select
          defaultValue={"COLLECTOR"}
        >
          {userTypeOptions.map(({ label, value }, i) => (
            <MenuItem key={label + i} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Create>
  );
};
